/*
 * configs/employee.js — the Employee entity config (recovered `Fe`/`Yt` from
 * entity-store.js): one concrete kind that exercises the whole Pattern-B
 * factory. Employee extends BaseEntity; its field set prepends 16 embedded
 * field descriptors; its itemCfg wires the `employee/*` record-CRUD routes.
 *
 * Reconstructed from lacak.io deployed behavior; app-specific contract verbatim
 * (type "employee"; defaultSortProperty "first_name"; letterField "first_name";
 * avatar { path:"employee/avatars/", iconPath:"./static/assets/images/employees/",
 * iconGrid:"repeat(5, 1fr)", defaultIcon:"person" }; permissions
 * {create/update:"employee_update"}; itemCfg routes employee/list ·
 * employee/read[value] · employee/update · employee/create · employee/delete ·
 * employee/avatar/upload · employee/avatar/assign; create/update body
 * {employee:<built>, force_reassign:true}; fetch {employee_id:+id};
 * delete {employee_id:id}; the 4 section labels + field_order arrays), internal
 * impl re-authored. Matches employee-entity-config.recovered.js.
 */

import { BaseEntity } from '../base-entity.js';
import { t } from '../i18n.js';

/** The 16 embedded field descriptors (recovered `EmployeeFields` / `Yt`). */
export function employeeEmbeddedFields() {
  return [
    { id: 'first_name', label: t('employees.fields.first_name'), type: 'text', embedded: true, required: true, hasAvatar: true, params: { maxLength: 100 } },
    { id: 'middle_name', label: t('employees.fields.middle_name'), type: 'text', embedded: true },
    { id: 'last_name', label: t('employees.fields.last_name'), type: 'text', embedded: true },
    { id: 'tracker_id', label: t('employees.fields.tracker_id'), type: 'tracker', embedded: true, params: { check_feature: 'app_tasks' } },
    { id: 'department_id', label: t('employees.fields.department_id'), type: 'select', embedded: true, params: { items: [], itemValue: 'id', itemText: 'label' } },
    { id: 'personnel_number', label: t('employees.fields.personnel_number'), type: 'text', embedded: true },
    { id: 'hardware_key', label: t('employees.fields.hardware_key'), type: 'text', embedded: true },
    { id: 'ssn', label: t('employees.fields.ssn'), type: 'text', embedded: true },
    { id: 'tags', label: t('employees.fields.tags'), type: 'tags', embedded: true },
    { id: 'phone', label: t('employees.fields.phone'), type: 'phone', embedded: true },
    { id: 'email', label: t('employees.fields.email'), type: 'email', embedded: true },
    { id: 'location', label: t('employees.fields.location'), type: 'location', embedded: true },
    { id: 'driver_license_number', label: t('employees.fields.driver_license_number'), type: 'text', embedded: true },
    { id: 'driver_license_cats', label: t('employees.fields.driver_license_cats'), type: 'tags', embedded: true },
    { id: 'driver_license_issue_date', label: t('employees.fields.driver_license_issue_date'), type: 'date', embedded: true, params: { maxDate: 'today' } },
    { id: 'driver_license_valid_till', label: t('employees.fields.driver_license_valid_till'), type: 'date', embedded: true },
  ];
}

/** Stamp the display name from the parts (recovered `formatEmployee`). */
export function formatEmployee(r) {
  if (!r) return '';
  return [r.first_name, r.middle_name, r.last_name].filter(Boolean).join(' ').trim();
}

export class Employee extends BaseEntity {
  constructor(attrs = {}) {
    super(attrs);
    this.type = 'employee';
    this.defaultSortProperty = 'first_name';
    this.letterField = 'first_name';
    this.formColumns = 2;
    this.hasFilter = false; // employees list uses search, not the filter popup
    this.hasPagination = true;
    this.localSearch = false;
    this.localSort = false;

    this.avatar = {
      style: 'border-radius:100%',
      path: 'employee/avatars/',
      iconPath: './static/assets/images/employees/',
      iconGrid: 'repeat(5, 1fr)',
      defaultIcon: 'person',
      colorMode: true,
      letterField: 'name',
    };

    this.permissions = { create: 'employee_update', update: 'employee_update' };

    // fired after successful CRUD (ctx = { store })
    this.callbacks = {
      create(ctx) { if (ctx && ctx.store) ctx.store.dispatch('employees/loadEmployees'); },
      update(ctx) { if (ctx && ctx.store) ctx.store.dispatch('employees/loadEmployees'); },
      delete(ctx) { if (ctx && ctx.store) ctx.store.dispatch('employees/loadEmployees'); },
    };

    this.titles = {
      screen: t('employees.screen_title'),
      update: t('employees.form.edit_title'),
      create: t('employees.form.create_title'),
    };

    // server-defined custom fields concat onto the embedded set (empty by default)
    this._customFields = [];
  }

  /* Employee's field set = 16 embedded + any server custom fields. */
  getFields() {
    return employeeEmbeddedFields().concat(this._customFields || []);
  }

  /* Build the `employee` payload from a flat record (recovered itemParamsBuilder). */
  itemParamsBuilder(item) {
    const keys = [
      'first_name', 'middle_name', 'last_name', 'tracker_id', 'department_id',
      'personnel_number', 'hardware_key', 'ssn', 'tags', 'phone', 'email', 'location',
      'driver_license_number', 'driver_license_cats', 'driver_license_issue_date',
      'driver_license_valid_till',
    ];
    const emp = {};
    keys.forEach((k) => { if (item[k] !== undefined) emp[k] = item[k]; });
    if (item.id != null && item.id !== '-') emp.id = item.id;
    return emp;
  }

  /* ---- itemCfg : the record-CRUD contract (Pattern B, verbatim routes) ---- */
  get itemCfg() {
    const self = this;
    return {
      routes() {
        return {
          list: 'employee/list',
          fetch: 'employee/read[value]', // [value] = result-key: take resp.value
          update: 'employee/update',
          create: 'employee/create',
          delete: 'employee/delete',
          avatar_upload: 'employee/avatar/upload',
          avatar_assign: 'employee/avatar/assign',
        };
      },
      paramsDecorators() {
        const build = (item) => {
          const emp = self.itemParamsBuilder(item);
          emp.email = emp.email || '';
          emp.phone = emp.phone || '';
          return { employee: emp, force_reassign: true };
        };
        return {
          fetch: () => ({ employee_id: +self.id }),
          create: build,
          update: build,
          delete: ({ id }) => ({ employee_id: id }),
          avatar_upload: (item, icon) => ({ employee_id: item.id, file: icon && icon.file && icon.file.file }),
          avatar_assign: (item, icon) => ({ employee_id: item.id, icon_id: icon && icon.icon_id }),
        };
      },
      responseDecorators() {
        return {
          list: (rows) => (rows || []).map((r) => ((r.name = formatEmployee(r)), r)),
          fetch: (r) => {
            if (!r) return r;
            r.name = formatEmployee(r);
            if (r.location) r.location.radius = r.location.radius || 150;
            return r;
          },
        };
      },
    };
  }

  /* Synthesize the descriptor layout CLIENT-SIDE (employees aren't server-designed).
   * Pass the root store to trigger the departments load, matching the recovered fetch(). */
  async fetch(store) {
    if (store && typeof store.dispatch === 'function') {
      try { await store.dispatch('departments/loadDepartments'); } catch (e) { /* soft */ }
    }
    this.set({
      id: '-',
      settings: {
        layout: {
          sections: [
            {
              label: t('employees.sections.main_fields'),
              field_order: ['first_name', 'middle_name', 'last_name', 'tracker_id',
                'department_id', 'personnel_number', 'hardware_key', 'ssn', 'tags'],
            },
            { label: t('employees.sections.contacts_fields'), field_order: ['phone', 'email'] },
            { label: t('employees.sections.address_fields'), field_order: ['location'] },
            {
              label: t('employees.sections.license_fields'),
              field_order: ['driver_license_number', 'driver_license_cats',
                'driver_license_issue_date', 'driver_license_valid_till'],
            },
          ],
        },
      },
      fields: null, // getFields() supplies the descriptors
    });
    return this;
  }

  getWelcomeHtml() { return t('employees.welcome_text'); }
}

/** Factory used by the store / registry: new descriptor, ready to fetch(). */
export function createEmployeeEntity(attrs = {}) {
  return new Employee(attrs);
}

export default Employee;
